// Embedding contract between the menu maker and the page that frames it
// (the Shopify page /pages/flavor-menu-maker). See README "Embedding".
//
// App -> host:  { source: 'wonderful-menu-maker', type: 'resize', height, layout }
//   Sent whenever the app's height changes. `height` is the full document
//   height in CSS px; `layout` is 'split' (side by side) or 'stacked'.
// Host -> app:  { source: 'wonderful-menu-maker-host', type: 'viewport', top, height }
//   Optional. `top` is the iframe's getBoundingClientRect().top in the host
//   page and `height` the host window's innerHeight. With it, the preview
//   stays in view while the host page scrolls past a full-height iframe.
// Host -> app:  { source: 'wonderful-menu-maker-host', type: 'hello' }
//   Optional. A host script that starts after the app has loaded asks for
//   the height again; the app answers with a resize message.
//
// public/embed.js is the host side, ready to load on any page.
// Nothing sensitive crosses the boundary (only sizes), so messages go to '*'.
import { RefObject, useEffect, useState } from 'react';

export const EMBED_SOURCE = 'wonderful-menu-maker';
export const HOST_SOURCE = 'wonderful-menu-maker-host';

export type EmbedLayout = 'split' | 'stacked';

export interface ResizeMessage {
  source: typeof EMBED_SOURCE;
  type: 'resize';
  height: number;
  layout: EmbedLayout;
}

export interface HostViewport {
  top: number;
  height: number;
}

export const isEmbedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws: we are framed.
    return true;
  }
};

export const postToHost = (message: ResizeMessage) => {
  try {
    window.parent.postMessage(message, '*');
  } catch {
    // Parent gone or blocked; nothing to do.
  }
};

/** Tells the framing page how tall the app is, every time that changes. */
export const useHostResize = (ref: RefObject<HTMLElement>, enabled: boolean, layout: EmbedLayout) => {
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    let last = -1;
    let pending: number | undefined;
    const send = () => {
      pending = undefined;
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height <= 0 || height === last) return;
      last = height;
      postToHost({ source: EMBED_SOURCE, type: 'resize', height, layout });
    };
    const schedule = () => {
      if (pending !== undefined) return;
      pending = window.setTimeout(send, 50);
    };

    // A host script that loads after us says hello: answer even if the height
    // has not changed since our last message, which it never received.
    const onHello = (event: MessageEvent) => {
      if (event.source !== window.parent || !isHostHello(event.data)) return;
      last = -1;
      schedule();
    };

    send();
    window.addEventListener('message', onHello);
    window.addEventListener('load', schedule);
    window.addEventListener('resize', schedule);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      observer.observe(el);
    }
    // Fonts and images can change the height after the first layout.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready?.then(schedule).catch(() => undefined);

    return () => {
      if (pending !== undefined) window.clearTimeout(pending);
      window.removeEventListener('message', onHello);
      window.removeEventListener('load', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [ref, enabled, layout]);
};

export const isHostHello = (data: unknown): boolean =>
  !!data && typeof data === 'object' && (data as Record<string, unknown>).source === HOST_SOURCE &&
  (data as Record<string, unknown>).type === 'hello';

export const parseViewportMessage = (data: unknown): HostViewport | null => {
  if (!data || typeof data !== 'object') return null;
  const message = data as Record<string, unknown>;
  if (message.source !== HOST_SOURCE || message.type !== 'viewport') return null;
  const top = Number(message.top);
  const height = Number(message.height);
  if (!Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return null;
  return { top, height };
};

/** Latest viewport the framing page reported, or null if it never sends one. */
export const useHostViewport = (enabled: boolean): HostViewport | null => {
  const [viewport, setViewport] = useState<HostViewport | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const next = parseViewportMessage(event.data);
      if (next) setViewport(next);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [enabled]);
  return viewport;
};
